import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { SituacaoContratoEnum } from '../enums/situacao-contrato.enum';
import { DataLayerData } from '../models/datalayer.model';
import { SituacaoContratoDataLayer } from './../models/datalayer.model';
import { Router, ActivatedRoute, NavigationEnd, Params } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

enum DomainEnum {
  CASASBAHIA_PRD = 'casasbahia',
  CASASBAHIA_HLG = 'cb',
  PONTOFRIO_PRD = 'pontofrio',
  PONTOFRIO_HLG = 'pf',
}

enum ReferrerEnum {
  GOOGLE = 'google.com',
  BING = 'bing.com',
  YAHOO = 'yahoo.com'
}

export interface UtmParameters {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) { }

  readonly domainToCookieMap: Record<DomainEnum, string> = {
    [DomainEnum.CASASBAHIA_PRD]: 'IPS-CasasBahia.com',
    [DomainEnum.CASASBAHIA_HLG]: 'IPS-CasasBahia.HLG.com',
    [DomainEnum.PONTOFRIO_PRD]: 'IPS-PontoFrio.com',
    [DomainEnum.PONTOFRIO_HLG]: 'IPS-PontoFrio.HLG.com',
  };

  readonly referrerToIpsMap: Record<ReferrerEnum, { ips: string, utm: object }> = {
    [ReferrerEnum.GOOGLE]: {
      ips: 'Parceiro=Google&Midia=BuscaOrganica&Campanha=DescontoEspecial',
      utm: { utm_source: 'Google', utm_medium: 'BuscaOrganica', utm_campaign: 'DescontoEspecial' }
    },
    [ReferrerEnum.BING]: {
      ips: 'Parceiro=Bing&Midia=BuscaOrganica&Campanha=DescontoEspecial',
      utm: { utm_source: 'Bing', utm_medium: 'BuscaOrganica', utm_campaign: 'DescontoEspecial' }
    },
    [ReferrerEnum.YAHOO]: {
      ips: 'Parceiro=Yahoo&Midia=BuscaOrganica&Campanha=DescontoEspecial',
      utm: { utm_source: 'Yahoo', utm_medium: 'BuscaOrganica', utm_campaign: 'DescontoEspecial' }
    }
  }

  readonly UTM_PARAMETES_KEY = 'utmp';

  pushNavigateEvent(nomePagina: string): void {
    window.dataLayer.push({
      event: 'update',
      pagina: {
        nomePagina,
        templatePagina: 'renegociacao',
      }
    });
  }

  pushEvent(dados: DataLayerData): void {
    window.dataLayer.push(dados);
  }

  situacaoContratoPipe(situacaoContrato: SituacaoContratoEnum) {
    switch (situacaoContrato) {
      case SituacaoContratoEnum.SITUACAO_EM_DIA:
        return SituacaoContratoDataLayer.PAGO;

      case SituacaoContratoEnum.SITUACAO_EM_ATRASO:
        return SituacaoContratoDataLayer.EM_ATRASO;

      case SituacaoContratoEnum.SITUACAO_AGUARDANDO_PAGAMENTO:
        return SituacaoContratoDataLayer.EM_ABERTO;
    }
  }

  updateIpsCookie(utmParams: UtmParameters): void {
    const { utm_source: source, utm_medium: medium } = utmParams;

    if (!source || !medium) {
      this.updateIpsCookieFromOrganicSearch();
      return;
    }

    const ipsValue = this.buildIpsValue(utmParams);
    const cookieName = this.getCookieNameForDomain();
    if (!cookieName) return;

    this.setCookie(cookieName, ipsValue, 30);
    this.saveUtmParametersObjectOnSessionStorage(utmParams);
  }

  updateIpsCookieFromOrganicSearch(): void {
    const organicResult = this.getIpsValueForOrganicSearch(document.referrer);
    if (!organicResult?.ips) return;

    const cookieName = this.getCookieNameForDomain();
    if (!cookieName) return;

    this.setCookie(cookieName, organicResult.ips, 30);
    this.saveUtmParametersObjectOnSessionStorage(organicResult.utm);
  }

  getCookieNameForDomain(): string | null {
    if (!environment.production) return this.domainToCookieMap.casasbahia;

    const domain = window.location.hostname;
    const domainKey = Object.values(DomainEnum).find(key => domain.toLowerCase().includes(key.toLowerCase()));
    return domainKey ? this.domainToCookieMap[domainKey] : null;
  }

  buildIpsValue(utmParams: UtmParameters): string {
    const { utm_source: source, utm_medium: medium, utm_campaign: campaign, utm_content: content, utm_term: term } = utmParams;
    let value = `Parceiro=${encodeURIComponent(source)}&Midia=${encodeURIComponent(medium)}`;
    if (campaign) value += `&Campanha=${encodeURIComponent(campaign)}`;
    if (content) value += `&Content=${encodeURIComponent(content)}`;
    if (term) value += `&Term=${encodeURIComponent(term)}`;
    return value;
  }

  getIpsValueForOrganicSearch(referrer: string): { ips: string, utm: object } | null {
    if (!referrer) return null;

    const referrerKey = Object.values(ReferrerEnum).find(key => referrer.toLowerCase().includes(key.toLowerCase()));
    return referrerKey ? this.referrerToIpsMap[referrerKey] : null;
  }

  setCookie(name: string, value: string, days: number): void {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "; expires=" + date.toUTCString();
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; Secure; SameSite=Lax`;
  }

  saveUtmParametersObjectOnSessionStorage(utmParams: UtmParameters): void {
    const utmParamsBase64 = btoa(JSON.stringify(utmParams));
    sessionStorage.setItem(this.UTM_PARAMETES_KEY, utmParamsBase64);
  }

  getUtmParametersObjectFromSessionStorage(): UtmParameters | null {
    const utmParamsBase64 = sessionStorage.getItem(this.UTM_PARAMETES_KEY);
    if (!utmParamsBase64) return null;

    try {
      const utmParams = JSON.parse(atob(utmParamsBase64));
      return utmParams;
    } catch (error) {
      console.error('Erro ao recuperar parâmetros UTM do sessionStorage:', error);
      return null;
    }
  }

  listenUTMParamsAndUpdateUrl(): Subscription {
    return this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe({
      next: () => {
        try {
          const currentRoute = this.getCurrentRoute(this.activatedRoute);
          const params = currentRoute?.snapshot?.queryParams || {};

          const utmParams: UtmParameters = {
            utm_source: params['utm_source'],
            utm_medium: params['utm_medium'],
            utm_campaign: params['utm_campaign'],
            utm_content: params['utm_content'],
            utm_term: params['utm_term'],
          };

          this.updateIpsCookie(utmParams);

          const storedUtmParams = this.getUtmParametersObjectFromSessionStorage() || {};

          const newQueryParams = { ...params, ...storedUtmParams };

          if (JSON.stringify(params) !== JSON.stringify(newQueryParams)) {
            this.router.navigate([], {
              queryParams: newQueryParams,
              replaceUrl: true,
            });
          }
        } catch (error) {
          console.warn('Erro no listenUTMParamsAndUpdateUrl:', error);
        }
      },
      error: (error) => {
        console.warn('Erro no fluxo do Observable UTM:', error);
      }
    });
  }


  private getCurrentRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

}
